
import { Component, Vue, Watch } from 'vue-property-decorator';
import { BlockStore, CommonStore, FloweringStore } from '@/store/modules';
import SvgIcon from '@/components/SvgIcon.vue';
import {
  FlowerEvent,
  RawFloweringPatchData
} from '@/store/models/flowering';
import QvInput from '@/components/FormComponents/QvInput.vue';
import QvSelect from '@/components/FormComponents/QvSelect.vue';
import helpers from '@/utils/helpers';
import { QTable } from 'quasar';
import { BlocksWithFruitCounts } from '@/store/models/block';
import { ValidationObserver } from 'vee-validate';
import { isNumber } from 'highcharts';
import { date, exportFile } from 'quasar';

@Component({
  components: {
    SvgIcon,
    QvSelect,
    QvInput,
    ValidationObserver
  }
})
export default class HarvestLoadCalculation extends Vue {
  $refs!: {
    table1: InstanceType<typeof QTable>;
    table2: InstanceType<typeof QTable>;
    table3: InstanceType<typeof QTable>;
  };
  rawFloweringEditable = false;
  maturityZoneEditable = false;
  countAndWeightEditable = false;
  denseTable = false;

  thumbStyle = {
    right: '4px',
    borderRadius: '5px',
    backgroundColor: '#bbb',
    height: '7px',
    opacity: 0.75
  };

  barStyle = {
    right: '2px',
    borderRadius: '9px',
    backgroundColor: '#bbb',
    height: '7px',
    opacity: 0.2
  };

  // eslint-disable-next-line
  tables: any[] = [];

  loadingHarvestDate = false;

  method = '';
  methods = [
    { value: '', label: 'All' },
    { value: 'M', label: 'Manual' },
    { value: 'MV', label: 'Machine Vision' }
  ];

  queryParams: { [key: string]: any } = {};

  get flowerEvents() {
    return FloweringStore.FlowerEvents;
  }

  get rawTransposedFlowerings() {
    return FloweringStore.RawTransposedFlowerings;
  }

  get rawFloweringData() {
    return this.rawTransposedFlowerings?.data ?? [];
  }

  get rawFloweringHarvestDate() {
    return FloweringStore.RawFloweringHarvestDate;
  }

  // Data of
  get blocksWithFruitCount() {
    return BlockStore.BlocksWithFruitCount;
  }

  getWeekOfYear(dt: string) {
    if (dt == '') {
      return '';
    }
    const dateObj = date.extractDate(dt, 'YYYY-MM-DD');
    return date.formatDate(dateObj, 'w');
  }

  get tableColumn() {
    if (this.rawTransposedFlowerings?.header) {
      const columns = this.rawTransposedFlowerings.header
        .filter(x => x && x != null && !['block'].includes(x))
        .map((x, index) => {
          return {
            name: index > 0 ? `w${index}` : x,
            label: index > 0 ? this.formatDate(x) : 'Date',
            r2:
              index > 0
                ? this.rawFloweringHarvestDate[x]
                  ? this.formatDate(this.rawFloweringHarvestDate[x])
                  : this.loadingHarvestDate ? 'Calculating harvest date' : 'Date not found'
                : 'Harvest',
            r3: index > 0 ? '' /*`W${this.getWeekOfYear(x)}`*/ : 'Block',
            field: (row?: any): string | number => index > 0 ? `w${index}` : x,
            sortable: x == 'blockName' ? true : false
          };
        });

      return columns;
    }
    return [];
  }

  get tableColumn2() {
    if (this.rawTransposedFlowerings?.header) {
      const columns = this.rawTransposedFlowerings.header
        .filter(x => x && !['block'].includes(x))
        .map((x, index) => ((console.log("tc2_x:",x,"Index:",index)),{
          name: index > 0 ? `mzw${index}` : x,
          label: index > 0 ? this.formatDate(x) : 'Flower Event',
          r2:
            index > 0
              ? this.rawFloweringHarvestDate[x]
                ? this.formatDate(this.rawFloweringHarvestDate[x])
                : this.loadingHarvestDate ? 'Calculating harvest date' : 'Date not found'
              : 'Harvest',
          r3: index > 0 ? '' /*`W${this.getWeekOfYear(x)}`*/ : 'Block',
          field: index > 0 ? `mzw${index}` : x,
          sortable: x == 'blockName' ? true : false
        }));
      return columns;
    }
    return [];
  }

  // TODO: The formula is wrong:
  //  maximumFruitLoad is in tons/ha, so the result should be *1000 to be in Kg
  //  The percentage is a value in [0;100] so we should /100 the result (this partially inhibit the previous conversion error)
  //  We may not need the percentage adjustment => To be checked before changing anything
  /*const*/ YPot = (row: BlocksWithFruitCounts) => {
    const floweringData = this.rawFloweringData.find(x => x.block == row.id)
    let yPot = 0;

    if (floweringData) {
      const maximumPercentTerminalsFlowered = this.maximumPercentTerminalsFlowered(floweringData);
      if (isFinite(maximumPercentTerminalsFlowered)) {
        yPot = row.area * row.maximumFruitLoad * maximumPercentTerminalsFlowered;
      }
    }
    return yPot;
  };

  // Fill "FRUIT COUNT AND WEIGHT" table
  get tableColumn3() {

    // Table headers (and totals)
    const columns = [
      {
        name: 'blockName',
        label: 'Block',
        r2: 'Block',
        r3: 'Total',
        field: 'blockName',
        sortable: true
      },
      {
        name: 'fruitsCount',
        label: 'Count',
        r2: '(No)',
        r3: this.blocksWithFruitCount.reduce((acc, x) => acc + x.fruitsCount, 0),
        field: 'fruitsCount'
      },
      {
        name: 'yield',
        label: 'Yield',
        r2: '(kg)',
        r3: Math.round(this.blocksWithFruitCount.reduce((acc,x) => acc + (x.fruitWeight! * x.fruitsCount || 0), 0)),
        field: (row: BlocksWithFruitCounts) => Math.round(row.fruitsCount * row.fruitWeight! || 0)
      },
      {
        name: 'yield_potential',
        label: 'Y<sub>POT</sub>',
        r2: '(Kg)',
        r3: Math.round(this.blocksWithFruitCount.reduce((acc,x) => acc + this.YPot(x), 0)),
        field: (row: BlocksWithFruitCounts) => Math.round(this.YPot(row))
      }
    ];

console.log('tableColumn3',columns);
    return columns;
  }

  get tableColumn4() {
    if (this.rawTransposedFlowerings?.header) {
      console.log("rawTransposedFlowerings:",this.rawTransposedFlowerings);
      const columns = this.rawTransposedFlowerings.header
        .filter(x => x && !['block'].includes(x))
        .map((x, index) => ({
          name: index > 0 ? `mzw${index}` : x,
          label:
            index > 0
              ? this.rawFloweringHarvestDate[x]
                ? this.formatDate(this.rawFloweringHarvestDate[x])
                : this.loadingHarvestDate	? 'Calculating harvest date' : 'Date not found'
              : 'Harvest',
          r2:
            index > 0
              ? `W${this.getWeekOfYear(this.rawFloweringHarvestDate[x] || '')}`
              : 'Harvest Week',
          r3:
            index > 0
              ? Math.round(this.yieldEstimation
                  .map((x: any) => (console.log("tc4_x",x),x[`mzw${index}`])|| 0)
                  .reduce((acc, x) => acc + x, 0))
              : 'Total',
          field: index > 0 ? `mzw${index}` : x,
          sortable: x == 'blockName' ? true : false
        }));
      return columns;
    }
    return [];
  }

  get visibleColumns() {
    if (this.rawTransposedFlowerings?.header) {
      const columns = this.rawTransposedFlowerings.header.filter(
        x => !['block_id', 'block'].includes(x)
      );
      return columns;
    }
    return [];
  }


  get yieldEstimation() {
    // Returns the array used as data-source for table 4
    // Used to return the yield estimation in number of fruits.
    // Now returns it in Kg as yield should be.
    console.log("yieldEstimation", this.rawFloweringData?.length, this.blocksWithFruitCount?.length);
    if (this.rawFloweringData?.length > 0 && this.blocksWithFruitCount?.length > 0) {
      return this.rawFloweringData.map((x:any) => {
        const block = this.blocksWithFruitCount.find(y => y.id == x.block);
        const blockFruitWeight:number = (block?.fruitsCount!||0) * (block?.fruitWeight!||0)
          || this.YPot(block!); // Use yPot instead of real data when they are not available yet

        const dt: {
          block: number;
          blockName: string;
          [key: string]: number | string | null;
        } = {
          block: x.block,
          blockName: x.blockName
        };

        for (let week = 1; week <= this.totalWeeks; ++week) {
          dt[`mzw${week}`] = (x[`mzw${week}`] === null) ? null : Math.round(x[`mzw${week}`] / this.maximumPercentTerminalsFlowered(x) * blockFruitWeight);
          console.log("dt["+week+"]",x[`mzw${week}`], this.maximumPercentTerminalsFlowered(x), blockFruitWeight);
        }
        console.log("DT:",dt);
        return dt;
      });
    }
    return [];
  }

  selected: FlowerEvent[] = [];

  flowerEvent: FlowerEvent | null = null;

  deleteDialog = false;
  multipleEditDialog = false;

  filter = '';

  loading = false;

  totalWeeks = 12;

  eventNumOptions = [
    { value: null, label: 'All' },
    { value: 1, label: '1' },
    { value: 2, label: '2' },
    { value: 3, label: '3' },
    { value: 4, label: '4' }
  ];
  eventNum: number | null = null;

  get currentFarm() {
    return CommonStore.SelectedFarmBlock;
  }
  pagination: {
    sortBy: string;
    descending: boolean;
    page: number;
    rowsPerPage: number;
  } = {
    sortBy: 'blockName',
    descending: false,
    page: 1,
    rowsPerPage: 0
  };

  get multipleEventNumberSelected() {
    const selectedEventNumbers = this.selected.map(x => x.eventNum);
    return (selectedEventNumbers.filter(x => x != this.selected[0].eventNum).length > 0);
  }

  // eslint-disable-next-line
  maximumPercentTerminalsFlowered(row: any) {
    const maxValList = [];
    let maxVal = 100;

    for (let week = 0; week < this.totalWeeks; week++) {
      if (row && row[`w${week + 1}`] && !isNaN(row[`w${week + 1}`])) {
        maxValList.push(parseInt(row[`w${week + 1}`]));
      }
    }
    maxVal = Math.max(...maxValList);
    return maxVal;
  }

  // eslint-disable-next-line
  validateMaturityZone(row: any, val: any, initialVal: any) {
    // eslint-disable-next-line
    let maxVal = this.maximumPercentTerminalsFlowered(row);
    let sum = 0;
    if (!val || val == '') {
      return true;
    }
    if (!isNaN(initialVal)) {
      sum = -initialVal;
    }
    val = parseInt(val);
    if (!isNaN(val)) {
      sum += val;
      for (let week = 0; week < this.totalWeeks; week++) {
        if (row[`mzw${week + 1}`] && !isNaN(row[`mzw${week + 1}`])) {
          sum += parseInt(row[`mzw${week + 1}`]);
        }
      }
      if (sum <= maxVal) {
        return true;
      }
    }
    return false;
  }
  onEventNumChange() {
    this.onRequest({
      pagination: {
        page: this.pagination.page,
        rowsPerPage: this.pagination.rowsPerPage,
        sortBy: this.pagination.sortBy,
        descending: this.pagination.descending
      },
      filter: this.filter
    });
  }

  // Return the fruit weight interval since we can now have a different fruit weight for each block
  get fruitWeightInterval() {
    if(this.blocksWithFruitCount?.length > 0) {
      let min = this.blocksWithFruitCount[0].fruitWeight!;
      let max = min;
      for(let i=1;i<this.blocksWithFruitCount.length; ++i) {
        const fw = this.blocksWithFruitCount[i].fruitWeight!;
        if(fw<min) min = fw;
        if(fw>max) max = fw;
      }
      return (min!=max) ? min.toFixed(2)+"-"+max.toFixed(2) : min.toFixed(2);
    }
    return "";
  }

  get firstFruitCount() {
    if (this.blocksWithFruitCount && this.blocksWithFruitCount.length > 0) {
      return this.blocksWithFruitCount[0];
    }
    return {} as BlocksWithFruitCounts;
  }

  get harvestCapacityQuantity() {
    if (this.firstFruitCount && this.firstFruitCount.harvestCapacityThreshold) {
      return (this.firstFruitCount.harvestCapacity / 100) * this.firstFruitCount.harvestCapacityThreshold;
    }
    return 0;
  }

  get startDate() {
    return CommonStore.StartDate;
  }

  get endDate() {
    return CommonStore.EndDate;
  }

  checkTarget(val: number | string) {
    if (val == 'Total' || this.blocksWithFruitCount.length <= 0) {
      return '';
    }
    if (val > this.firstFruitCount.harvestCapacity + this.harvestCapacityQuantity) {
      return 'bg-red-1';
    }
    else if (val < this.firstFruitCount.harvestCapacity - this.harvestCapacityQuantity) {
      return 'bg-blue-1';
    }
    return 'bg-green-1';
  }

  get tableArrayData() {
    const tableColumn = this.tableColumn;
    const tableColumn2 = this.tableColumn2;
    const tableColumn3 = this.tableColumn3;
    const tableColumn4 = this.tableColumn4;
    const content = [
      tableColumn.map(col => helpers.wrapCsvValue(col.name == 'blockName' ? 'FLOWERING DATA (% terminals flowered)' : ''))
        .concat(tableColumn2.map(col => helpers.wrapCsvValue(col.name == 'blockName' ? 'FLOWERING CONDENSED TO FLOWERING EVENTS (FE) (MATURITY ZONES)' : '')))
        .concat(tableColumn3.map(col => helpers.wrapCsvValue(col.name == 'blockName' ? 'FRUIT COUNT AND WEIGHT' : '')))
        .concat(tableColumn4.map(col => helpers.wrapCsvValue(col.name == 'blockName' ? 'YIELD ESTIMATION BY WEEK' : ''))),
      tableColumn
        .map(col => helpers.wrapCsvValue(col.label))
        .concat(tableColumn2.map(col => helpers.wrapCsvValue(col.label)))
        .concat(tableColumn3.map(col => helpers.wrapCsvValue(col.label)))
        .concat(tableColumn4.map(col => helpers.wrapCsvValue(col.label))),
      tableColumn
        .map(col => helpers.wrapCsvValue(col.r2))
        .concat(tableColumn2.map(col => helpers.wrapCsvValue(col.r2)))
        .concat(tableColumn3.map(col => helpers.wrapCsvValue(col.r2)))
        .concat(tableColumn4.map(col => helpers.wrapCsvValue(col.r2))),
      tableColumn
        .map(col => helpers.wrapCsvValue(col.r3))
        .concat(tableColumn2.map(col => helpers.wrapCsvValue(col.r3)))
        .concat(tableColumn3.map(col => helpers.wrapCsvValue(col.r3)))
        .concat(tableColumn4.map(col => helpers.wrapCsvValue(col.r3)))
    ].concat(
      // eslint-disable-next-line
      this.rawFloweringData.map((row: any) => {
        const r = tableColumn
          .map(col => helpers.wrapCsvValue(row[col.name]))
          .concat(tableColumn2.map(col => helpers.wrapCsvValue(row[col.name])))
          .concat(
            tableColumn3.map(col => {
              // eslint-disable-next-line
              const data: any = this.blocksWithFruitCount.find(x => x.id == row.block);
              return data
                ? helpers.wrapCsvValue(
                  typeof col.field === 'function'
                    ? col.field(data)
                    : data[col.field === void 0 ? col.name : col.field]
                )
                : '';
            })
          )
          .concat(
            tableColumn4.map(col => {
              // eslint-disable-next-line
              const data: any = this.yieldEstimation.find(x => x.block == row.block);
              return data ? helpers.wrapCsvValue(data[col.name]) : '';
            })
          )
          .join(',');
        return r;
      })
    );
console.log('tableArrayData',content)
    return content;
  }

  exportTable() {
    // naive encoding to csv format
    const content = this.tableArrayData.join('\r\n');

    const status = exportFile(
      'harvest-load-calculation.csv',
      content,
      'text/csv'
    );

    if (status !== true) {
      this.$q.notify({
        message: 'Browser denied file download...',
        color: 'negative',
        icon: 'warning'
      });
    }
  }

  //eslint-disable-next-line
  async onRequest(props: any) {
    const { page, rowsPerPage, sortBy, descending } = props.pagination;
    const filter = props.filter;
    this.loading = true;

    const ordering = `${descending ? '-' : ''}${sortBy}`;

    this.queryParams = {
      page: page,
      search: filter,
      eventNum: this.eventNum,
      ordering: ordering,
      pageSize: rowsPerPage,
      farm: this.currentFarm.farmId,
      startDate: this.startDate,
      endDate: this.endDate,
      method: this.method
    };
    BlockStore.getAllMyBlocksWithFruitCounts(this.queryParams);
    this.loadingHarvestDate = true;
    FloweringStore.getRawFloweringHarvestDate(this.queryParams).then(() => {
      this.loadingHarvestDate = false;
    });
    await FloweringStore.getRawTransposedFlowerings(this.queryParams);
    this.loading = false;
  }

  async onDelete(flowerEventId: number) {
    this.deleteDialog = false;
    if (flowerEventId != 0) {
      FloweringStore.deleteFlowerEvent(flowerEventId);
      FloweringStore.setAddEditFlowerEvent({} as FlowerEvent);
    }
  }

  deleteFlowerEvent(flowerEvent: FlowerEvent) {
    this.flowerEvent = flowerEvent;
    this.deleteDialog = true;
  }

  editFlowerEvent(flowerEvent: FlowerEvent) {
    this.flowerEvent = flowerEvent;
    FloweringStore.setAddEditFlowerEvent(flowerEvent);
  }

  editMultiple() {
    const selected = this.selected.map(x => x.id);
    this.$router.push({
      name: 'EditMultipleFlowerEvent',
      params: {
        flowerEventIds: `[${selected.join(',')}]`
      }
    });
  }

  // eslint-disable-next-line
  async updateRawFloweringData(newValue: number | string, column: any, row: any) {
    console.log('updateRawFloweringData:', newValue, column, row);
    const id = row.id;
    const data: RawFloweringPatchData = { id: id };
    if (newValue == '') {
      data[column['name']] = null;
    } else {
      data[column['name']] = newValue;
    }
    await FloweringStore.updateRawFloweringData(data);
  }

  async updateCountData(newValue: number | string, column: any, row: any) {
    console.log('updateCountData:', newValue, column, row);
    const id = row.id;
    if(column.name=="yield") { // Estimate number of fruits to get the correct yield
      const newWeight = Number(newValue);
      if(isFinite(newWeight) && row.fruitWeight != 0)
      {
        row.fruitsCount = Math.round(newWeight/row.fruitWeight);
      }
    }
    // Update previously stored yield value to show up after count has been edited
    row.yield = row.fruitsCount * row.fruitWeight;
  }

  async beforeCreate() {
    await helpers.fetchingDefaultFarm();
    this.loadRequest();
  }

  // eslint-disable-next-line
  scrollHandler(event: any) {
    window.requestAnimationFrame(() => {
      const scrollTop = event.target.scrollTop;
      const scrollLeft = event.target.scrollLeft;

      for (const table of this.tables) {
        table.scrollTop = scrollTop;
        table.scrollLeft = scrollLeft;
      }
    });
  }

  recomputeHarvestDates() {
    const queryParams = {
      ...this.queryParams,
      recomputeHarvestDates: true
    };
    this.loadingHarvestDate = true;
    FloweringStore.getRawFloweringHarvestDate(queryParams).then(() => {
      this.loadingHarvestDate = false;
    });
  }

  async loadRequest() {
    this.loading = true;
    await this.onRequest({
      pagination: this.pagination,
      filter: this.filter
    });
    this.loading = false;
  }

  @Watch('startDate')
  onStartDateChange() {
    this.loadRequest();
  }

  @Watch('endDate')
  onEndDateChange() {
    this.loadRequest();
  }

  // For logging in "v-" statements
  // Usage: Replace v-xxx="expression" with v-xxx="__con.log('msg') ?? expression"
  __con() {
    return window.console;
  }

  // Convert "yyyy-mm-dd" dates into "mm-dd"
  formatDate = (x:string|null) => x?.split('-').slice(1).join('-') ?? "";

  mounted() {
    const tables = document.getElementsByClassName('q-table__middle scroll');
    for (const table of tables) {
      table.addEventListener('scroll', this.scrollHandler);
      this.tables.push(table);
    }
  }
}
